import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Avatar,
  IconButton,
  Skeleton,
  Tooltip,
  Divider,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import {
  Work as WorkIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Search as SearchIcon,
  Psychology as PsychologyIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  SmartToy as AgentIcon,
  PersonAdd as PersonAddIcon,
  Event as EventIcon,
  Send as SendIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
} from "@mui/icons-material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

// Add CSS for spinning animation
const spinAnimation = `
  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

// Inject the CSS
const style = document.createElement("style");
style.textContent = spinAnimation;
document.head.appendChild(style);

// Stat Card Component
const StatCard = ({ title, value, icon, color, subtitle, trend, loading }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <Card sx={{ height: "100%", position: "relative", overflow: "visible" }}>
      <CardContent>
        {/* Top row: Icon on left, Trend on right */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Avatar sx={{ bgcolor: color, width: 48, height: 48 }}>{icon}</Avatar>
          {trend && !loading && (
            <Typography
              variant="body2"
              sx={{
                color: trend.includes("+") ? "success.main" : "error.main",
                fontWeight: 600,
              }}
            >
              {trend}
            </Typography>
          )}
        </Box>

        {loading ? (
          <Skeleton variant="text" width="60%" height={40} />
        ) : (
          <>
            {/* Centered value */}
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 1,
                color: "text.primary",
                textAlign: "center",
              }}
            >
              {value}
            </Typography>
            {/* Centered title in one line */}
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontWeight: 500,
                textAlign: "center",
              }}
            >
              {title}
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  </motion.div>
);

// Segmented Circular Progress Component
const SegmentedCircularProgress = ({ value, size = 96 }) => {
  const segments = 4;
  const filledSegments = Math.floor((value / 100) * segments);
  const radius = 40;
  const center = size / 2;
  const strokeWidth = 8;
  const gapDegrees = 8; // Gap between segments in degrees

  const getArcPath = (startAngle, endAngle) => {
    const startRad = ((startAngle - 90) * Math.PI) / 180;
    const endRad = ((endAngle - 90) * Math.PI) / 180;
    const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

    const x1 = center + radius * Math.cos(startRad);
    const y1 = center + radius * Math.sin(startRad);
    const x2 = center + radius * Math.cos(endRad);
    const y2 = center + radius * Math.sin(endRad);

    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`;
  };

  return (
    <Box sx={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size}>
        {/* Background track - full circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="#E5E7EB"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {/* Segmented progress */}
        {Array.from({ length: segments }).map((_, index) => {
          const isFilled = index < filledSegments;
          const segmentAngle = 360 / segments;
          const startAngle = index * segmentAngle + gapDegrees / 2;
          const endAngle = (index + 1) * segmentAngle - gapDegrees / 2;

          return (
            <path
              key={index}
              d={getArcPath(startAngle, endAngle)}
              fill="none"
              stroke={isFilled ? "#1976d2" : "#E5E7EB"}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
            />
          );
        })}
      </svg>
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          component="div"
          sx={{
            fontSize: "16px",
            fontWeight: 400,
            color: "#000000",
            lineHeight: "24px",
          }}
        >
          {value}%
        </Typography>
      </Box>
    </Box>
  );
};

// Batch Card Component with Circular Progress
const BatchCard = ({ role, candidates, progress = 75, loading }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.3 }}
    style={{ width: "100%", height: "100%" }}
  >
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: "12px",
        border: "1px solid #E5E7EB",
        boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
      }}
    >
      <CardContent
        sx={{
          textAlign: "center",
          p: 2.5,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          "&:last-child": { pb: 2.5 },
        }}
      >
        {loading ? (
          <>
            <Skeleton
              variant="circular"
              width={96}
              height={96}
              sx={{ mx: "auto", mb: 2 }}
            />
            <Skeleton
              variant="text"
              width="80%"
              height={20}
              sx={{ mx: "auto", mb: 1 }}
            />
            <Skeleton variant="text" width="60%" height={16} />
          </>
        ) : (
          <>
            <Box sx={{ mb: 2.5 }}>
              <SegmentedCircularProgress value={progress} size={96} />
            </Box>
            <Typography
              variant="h6"
              sx={{
                mb: 1.5,
                fontWeight: 600,
                fontSize: "16px",
                lineHeight: "24px",
                color: "#101828",
                fontFamily: "inherit",
              }}
            >
              {role}
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: "16px",
                  lineHeight: "24px",
                  fontWeight: 400,
                  color: "#4A5565",
                  fontFamily: "inherit",
                }}
              >
                {candidates}
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  lineHeight: "24px",
                  fontWeight: 400,
                  color: "#4A5565",
                  fontFamily: "inherit",
                }}
              >
                candidates
              </Typography>
            </Box>
          </>
        )}
      </CardContent>
    </Card>
  </motion.div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState("1");

  // Mock data for the new dashboard features
  const platformData = [
    { name: "LinkedIn", leads: 780 },
    { name: "Apollo", leads: 500 },
    { name: "Google Maps", leads: 400 },
    { name: "Instagram", leads: 230 },
    { name: "Email DB", leads: 180 },
  ];

  const leadProgressData = [
    { source: "LinkedIn", current: 450, target: 500, percentage: 90 },
    { source: "Apollo", current: 380, target: 400, percentage: 95 },
    { source: "Google Maps", current: 280, target: 350, percentage: 80 },
    { source: "Instagram", current: 120, target: 150, percentage: 80 },
  ];

  const recentBatches = [
    { role: "Senior Frontend Developer", candidates: 52, progress: 75 },
    { role: "Backend Engineer", candidates: 38, progress: 75 },
    { role: "DevOps Specialist", candidates: 29, progress: 75 },
  ];

  // Manual refresh function (no API call)
  const handleRefresh = () => {
    setIsRefreshing(true);
    // Simulate refresh delay
    setTimeout(() => {
      setIsRefreshing(false);
    }, 500);
  };

  // Calculate statistics - using mock data for now to match the first image
  const totalLeadsDiscovered = 1247;
  const qualifiedLeads = 342;
  const meetingsBooked = 87;
  const outreachSent = 892;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Scrollable Container - Header and Content together */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          overflowX: "hidden",
          minHeight: 0,
          paddingBottom: { 
            xs: "calc(60px + env(safe-area-inset-bottom, 0px))", 
            sm: "calc(40px + env(safe-area-inset-bottom, 0px))", 
            md: "0px" 
          },
          WebkitOverflowScrolling: "touch",
          marginBottom: { xs: "0px", md: "0px" },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "24px 24px 0 24px",
          }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Welcome back! Here's your recruitment overview.
            </Typography>
          </Box>
          {/* hi */}
          <Tooltip title="Refresh Data">
            <IconButton
              onClick={handleRefresh}
              color="primary"
              disabled={isRefreshing}
            >
              <RefreshIcon
                sx={{
                  animation: isRefreshing ? "spin 1s linear infinite" : "none",
                }}
              />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Statistics Cards */}
        <Box
          sx={{
            padding: "24px",
          }}
        >
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Total Leads Discovered"
                value={totalLeadsDiscovered.toLocaleString()}
                icon={<PeopleIcon />}
                color="primary.main"
                trend="+18%"
                loading={isRefreshing}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Qualified Leads"
                value={qualifiedLeads.toLocaleString()}
                icon={<CheckCircleOutlineIcon />}
                color="success.main"
                trend="+23%"
                loading={isRefreshing}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Meetings Booked"
                value={meetingsBooked.toLocaleString()}
                icon={<EventIcon />}
                color="info.main"
                trend="+15%"
                loading={isRefreshing}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Outreach Sent"
                value={outreachSent.toLocaleString()}
                icon={<SendIcon />}
                color="secondary.main"
                trend="+28%"
                loading={isRefreshing}
              />
            </Grid>
          </Grid>

          {/* Platform Analytics Section */}
          <Grid container spacing={3} sx={{ mb: 4, alignItems: "stretch" }}>
            <Grid item xs={12} md={8}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <CardContent
                  sx={{ flex: 1, display: "flex", flexDirection: "column" }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 3,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, mb: 0.5 }}
                      >
                        Platform Analytics
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Lead sourcing by platform
                      </Typography>
                    </Box>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <Select
                        value={selectedBatch}
                        onChange={(e) => setSelectedBatch(e.target.value)}
                      >
                        <MenuItem value="1">Batch number 1</MenuItem>
                        <MenuItem value="2">Batch number 2</MenuItem>
                        <MenuItem value="3">Batch number 3</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ width: "100%", flex: 1, minHeight: 400 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={platformData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <RechartsTooltip />
                        <Legend />
                        <Bar dataKey="leads" fill="#1976d2" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Right Column: Lead Discovery Progress and Recently Accessed Batches */}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  height: "100%",
                }}
              >
                {/* Lead Discovery Progress by Source */}
                <Card sx={{ flex: 1 }}>
                  <CardContent sx={{ pb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Lead Discovery Progress by Source
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      {leadProgressData.map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            mb: index === leadProgressData.length - 1 ? 0 : 2,
                            pb: 0,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {item.source}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {item.current} / {item.target} leads
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 0,
                            }}
                          >
                            <LinearProgress
                              variant="determinate"
                              value={item.percentage}
                              sx={{
                                flex: 1,
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: "grey.200",
                                "& .MuiLinearProgress-bar": {
                                  backgroundColor:
                                    item.percentage >= 90
                                      ? "success.main"
                                      : "primary.main",
                                },
                              }}
                            />
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ minWidth: 40, textAlign: "right" }}
                            >
                              {item.percentage}%
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>

                {/* Recently Accessed Batches */}
                <Card
                  sx={{ flex: 1, display: "flex", flexDirection: "column" }}
                >
                  <CardContent
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      pb: 2,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Recently Accessed Batches
                    </Typography>
                    <Grid
                      container
                      spacing={1.5}
                      sx={{ flex: 1, alignItems: "stretch" }}
                    >
                      {recentBatches.map((batch, index) => (
                        <Grid item xs={4} key={index} sx={{ display: "flex" }}>
                          <BatchCard
                            role={batch.role}
                            candidates={batch.candidates}
                            progress={batch.progress}
                            loading={isRefreshing}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
